import { useState } from "react";
import type { CalcArray, CalcMatrix } from "@quanta/calcscript";

interface MatrixViewerProps {
  value: CalcMatrix | CalcArray;
  name?: string;
  onClose: () => void;
}

// Type guard to detect if it's a 2D matrix
function isMatrix(value: CalcMatrix | CalcArray): value is CalcMatrix {
  return Array.isArray(value) && value.length > 0 && Array.isArray(value[0]);
}

export function MatrixViewer({ value, name, onClose }: MatrixViewerProps) {
  const [decimalPlaces, setDecimalPlaces] = useState(4);

  const formatNumber = (n: number): string => {
    if (!Number.isFinite(n)) {
      return n > 0 ? "∞" : n < 0 ? "-∞" : "NaN";
    }
    if (Number.isInteger(n) && Math.abs(n) < 1e10) {
      return n.toString();
    }
    return n.toFixed(decimalPlaces);
  };

  const is2D = isMatrix(value);
  // Convert value to 2D array format for display
  const rows: number[][] = is2D ? (value as number[][]) : [value as number[]];
  const numRows = is2D ? rows.length : 1;
  const numCols = is2D ? (rows[0]?.length ?? 0) : (value as number[]).length;

  return (
    <div className="matrix-viewer-overlay" onClick={onClose}>
      <div className="matrix-viewer" onClick={(e) => e.stopPropagation()}>
        <div className="matrix-viewer-header">
          <div className="matrix-viewer-title">
            {name && <span className="matrix-viewer-name">{name}</span>}
            <span className="matrix-viewer-dims">
              {numRows}×{numCols} {is2D ? "matrix" : "array"}
            </span>
          </div>
          <div className="matrix-viewer-controls">
            <label>
              Decimals:
              <select
                value={decimalPlaces}
                onChange={(e) => setDecimalPlaces(parseInt(e.target.value))}
              >
                <option value={2}>2</option>
                <option value={4}>4</option>
                <option value={6}>6</option>
                <option value={8}>8</option>
                <option value={10}>10</option>
                <option value={15}>15 (full)</option>
              </select>
            </label>
            <button className="matrix-viewer-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className="matrix-viewer-content">
          <table className="matrix-table">
            <thead>
              <tr>
                <th className="row-header"></th>
                {Array.from({ length: numCols }, (_, i) => (
                  <th key={i} className="col-header">
                    {i + 1}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => (
                <tr key={rowIdx}>
                  <td className="row-header">{rowIdx + 1}</td>
                  {row.map((val, colIdx) => (
                    <td key={colIdx} className="matrix-cell">
                      {typeof val === "number"
                        ? formatNumber(val)
                        : String(val)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
